import { createRouter, createWebHistory } from 'vue-router'
import { supabase } from '../services/supabase'
import { useSubscriptionStore } from '../stores/subscription'
import { analytics } from '../utils/analytics'
import ConnectionsView from '../views/ConnectionsView.vue'
import Home from '../views/Home.vue'
import Admin from '../views/Admin.vue'

const routes = [
  {
    path: '/',
    name: 'Landing',
    component: () => import('../views/Landing.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Home,
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/connections',
    name: 'Connections',
    component: ConnectionsView,
    meta: { requiresAuth: true }
  },
  {
    path: '/generated-content',
    name: 'generated-content',
    component: () => import('../views/GeneratedContent.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/article/:id',
    name: 'article-view',
    component: () => import('../views/ArticleView.vue'),
    meta: { requiresAuth: true, requiresSubscription: true }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('../views/Pricing.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: Admin,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue')
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/Register.vue')
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue')
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import('../views/Account.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('../views/AuthCallback.vue')
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/Privacy.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/Terms.vue'),
    meta: { requiresAuth: false }
  },
  // Catch 404s
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Check for Stripe session completion
router.beforeEach(async (to, from, next) => {
  if (to.path === '/profile' && to.query.session_id) {
    const subscriptionStore = useSubscriptionStore()
    try {
      await subscriptionStore.refreshSubscription()
      next()
      return
    } catch (error) {
      next('/pricing')
      return
    }
  }

  // Check authentication
  const { data: { session } } = await supabase.auth.getSession()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // If auth is required and user is not logged in
  if (requiresAuth && !session) {
    next('/login')
    return
  }

  // If user is logged in and trying to access login/register pages
  if (session && (to.path === '/login' || to.path === '/register')) {
    next('/dashboard')
    return
  }

  // If route requires subscription
  if (session && to.matched.some(record => record.meta.requiresSubscription)) {
    const subscriptionStore = useSubscriptionStore()
    await subscriptionStore.initializeUser()

    if (!subscriptionStore.canSearch && !subscriptionStore.canCreateContent) {
      next('/pricing')
      return
    }
  }

  // If route requires admin access
  if (to.meta.requiresAdmin) {
    const adminStatus = await isAdmin()
    if (!adminStatus) {
      next('/')
      return
    }
  }

  next()
})

// Track page views
router.afterEach((to) => {
  analytics.logPageView(to.fullPath)
})

// Check if user is admin
async function isAdmin() {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return false

    const { data: subscriptionData, error } = await supabase
      .from('user_subscriptions')
      .select('tier')
      .eq('user_id', session.user.id)
      .single()

    if (error) {
      console.error('Error checking admin status:', error)
      return false
    }

    return subscriptionData?.tier === 'admin'
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

export default router
